import { useState } from 'react';
import { useUserSelector } from '~/hooks';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import {
  CandidateStatus,
  JobPostingStatus,
  useSaveJobPostDetail,
  useUpdateCandidateStatus,
} from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { IFeedback } from '../../Types';

/**
 * Close Job Flow Steps:
 * 1. When user clicks "Close Job":
 *    - If has candidates → Show SelectCandidatesPopup
 *    - If no candidates → Show ConfirmCloseJobPopup
 *
 * 2. On SelectCandidatesPopup (if has candidates):
 *    - Select hired candidates (optional) and proceed to close job → Show ConfirmCloseJobPopup
 *
 * 3. On ConfirmCloseJobPopup:
 *    - Click "Go Back" → If has candidates: show SelectCandidatesPopup | If no candidates: dismiss popup and end flow
 *    - Click "Close Job" → Show CloseJobFeedbackPopup
 *
 * 4. On CloseJobFeedbackPopup:
 *    - Click "Skip" or "Submit" → Both proceed to CloseJobSuccessPopup
 *
 * 5. On CloseJobSuccessPopup:
 *    - Click "Done" → Dismiss popup and end flow
 */

type TPopupStep = 'select-candidates' | 'confirm-close' | 'feedback' | 'success' | null;

interface ICloseJobFlowState {
  currentStep: TPopupStep;
  selectedCandidateIds: string[];
}

interface IUseCloseJobFlowParams {
  postingId?: string;
  hasCandidatesToHire: boolean;
}

/**
 * Hook to manage the close job flow state machine
 * Handles navigation between different popup steps and tracks selected candidates
 */
export const useCloseJobFlow = ({ postingId, hasCandidatesToHire }: IUseCloseJobFlowParams) => {
  const user = useUserSelector();
  const { mutateAsync: updateJobStatusAsync, isPending: isClosingJob } = useSaveJobPostDetail();
  const { mutateAsync: updateCandidateStatusAsync, isPending: isUpdatingCandidates } =
    useUpdateCandidateStatus({ invalidateCache: false });
  const [state, setState] = useState<ICloseJobFlowState>({
    currentStep: null,
    selectedCandidateIds: [],
  });

  const startFlow = () => {
    setState({
      currentStep: hasCandidatesToHire ? 'select-candidates' : 'confirm-close',
      selectedCandidateIds: [],
    });
  };

  const handleSelectCandidates = (candidateIds: string[]) => {
    setState((prev) => ({
      ...prev,
      currentStep: 'confirm-close',
      selectedCandidateIds: candidateIds,
    }));
  };

  const handleGoBack = () => {
    if (hasCandidatesToHire) {
      setState((prev) => ({
        ...prev,
        currentStep: 'select-candidates',
      }));
    } else {
      closeFlow();
    }
  };

  const handleConfirmClose = async () => {
    if (!postingId) return;

    try {
      // First, Mark candidates as hired if any selected
      if (state.selectedCandidateIds.length > 0) {
        await updateCandidateStatusAsync({
          candidateIds: state.selectedCandidateIds,
          status: CandidateStatus.Hired,
        });
      }

      // Second, Mark job as closed
      await updateJobStatusAsync({
        id: postingId,
        payload: { status: JobPostingStatus.Closed },
      });

      // Lastly, Move to feedback step
      setState((prev) => ({
        ...prev,
        currentStep: 'feedback',
      }));
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const handleFeedbackSkip = () => {
    setState((prev) => ({
      ...prev,
      currentStep: 'success',
    }));
  };

  const handleFeedbackSubmit = (feedback: IFeedback) => {
    // Track PostHog event for close job feedback submission
    trackPosthogEvent(POSTHOG_EVENTS.OFFICE_PERM_CLOSE_JOB_FEEDBACK_SUBMITTED, {
      user_id: user?.id,
      posting_id: postingId,
      positive_feedback: feedback.positive,
      positive_feedback_count: feedback.positive?.length || 0,
      negative_feedback: feedback.negative,
      negative_feedback_count: feedback.negative?.length || 0,
      other_feedback: feedback.otherText || '',
      has_hires: state.selectedCandidateIds.length > 0,
    });

    setState((prev) => ({
      ...prev,
      currentStep: 'success',
    }));
  };

  const closeFlow = () => {
    setState({
      currentStep: null,
      selectedCandidateIds: [],
    });
  };

  return {
    currentStep: state.currentStep,
    selectedCandidateIds: state.selectedCandidateIds,
    hasHires: state.selectedCandidateIds.length > 0,
    isClosingJob: isClosingJob || isUpdatingCandidates,
    startFlow,
    handleSelectCandidates,
    handleGoBack,
    handleConfirmClose,
    handleFeedbackSkip,
    handleFeedbackSubmit,
    closeFlow,
  };
};
